/**
 * Conversation Transfer — report data.
 *
 * One row per hand-off. Paging is client-side (the page slices what is already
 * in hand), so the whole bounded window is fetched once rather than re-queried
 * per page — the same posture as the other reports.
 */
import { useCallback, useState } from 'react'
import { api } from '../api/client.js'

export interface ConversationTransferFilters {
  timezone: string
  interval: string
  startDate: string
  endDate: string
  startTime: string
  endTime: string
  locationIds: string[]
  branchIds: string[]
  departmentIds: string[]
}

export function buildTransferParams(filters: ConversationTransferFilters): URLSearchParams {
  const params = new URLSearchParams({
    timezone: filters.timezone,
    interval: filters.interval,
    startDate: filters.startDate,
    endDate: filters.endDate,
    startTime: filters.startTime,
    endTime: filters.endTime,
  })
  if (filters.locationIds.length > 0) params.set('locationIds', filters.locationIds.join(','))
  if (filters.branchIds.length > 0) params.set('branchIds', filters.branchIds.join(','))
  if (filters.departmentIds.length > 0) {
    params.set('departmentIds', filters.departmentIds.join(','))
  }
  return params
}

interface UseReportResult {
  rows: Record<string, unknown>[]
  total: number
  loading: boolean
  error: string | null
  hasRun: boolean
  run: (filters: ConversationTransferFilters) => Promise<void>
  reset: () => void
}

export function useConversationTransferReport(): UseReportResult {
  const [rows, setRows] = useState<Record<string, unknown>[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasRun, setHasRun] = useState(false)

  const run = useCallback(async (filters: ConversationTransferFilters): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const params = buildTransferParams(filters)
      params.set('limit', '1000')
      const res = await api.get<{ rows: Record<string, unknown>[]; total: number }>(
        `/reports/conversations/transfers?${params.toString()}`,
      )
      setRows(res.rows ?? [])
      setTotal(res.total ?? 0)
      setHasRun(true)
    } catch (err) {
      setRows([])
      setTotal(0)
      setHasRun(true)
      setError(err instanceof Error ? err.message : 'Could not load the report.')
    } finally {
      setLoading(false)
    }
  }, [])

  const reset = useCallback((): void => {
    setRows([])
    setTotal(0)
    setHasRun(false)
    setError(null)
  }, [])

  return { rows, total, loading, error, hasRun, run, reset }
}
